import { Combobox } from "@headlessui/react"
import { MapPinIcon } from "@heroicons/react/24/solid"
import capitalize from "lodash/capitalize"
import React, { useEffect, useRef, useState } from "react"
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService"
import { twMerge } from "tailwind-merge"

import { BASE_INPUT_CLASSNAMES, DROPDOWN_CLASSNAMES, DROPDOWN_OPTION_CLASSNAMES } from "../../components/shared/Inputs"
import LoadingSpinner from "../../components/shared/LoadingSpinner"
import useWindowSize from "../../hooks/useWindowSize"
import { stripUSAFromString } from "../../utils/utils"

const LocationSearchBox = () => {
  const { width } = useWindowSize()
  const isMobile = width <= 768
  const [inputText, setInputText] = useState("")
  const [isResolving, setIsResolving] = useState(false)
  const skipBlurResolveRef = useRef(false)
  const lastAppliedLabelRef = useRef("")

  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } = usePlacesService({
    debounce: 100,
    options: {
      types: ["locality", "administrative_area_level_3", "postal_code"]
    }
  })

  useEffect(() => {
    const onNewLocationChosen = (e) => {
      setInputText(e.detail)
      lastAppliedLabelRef.current = e.detail || ""
    }
    document.addEventListener("newLocationChosen", onNewLocationChosen)

    return () => {
      document.removeEventListener("newLocationChosen", onNewLocationChosen)
    }
  }, [])

  const applyMapCenter = (lat, lng, label) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return

    const newMapCenter = { lat, lng }
    document.dispatchEvent(new CustomEvent("newMapCenter", { detail: newMapCenter }))

    const displayLabel = stripUSAFromString(label || "")
    if (displayLabel) {
      setInputText(displayLabel)
      lastAppliedLabelRef.current = displayLabel
    }
  }

  const applyPrediction = (item) => {
    if (!item?.place_id || !placesService) return

    setIsResolving(true)
    placesService.getDetails({ placeId: item.place_id }, (place, status) => {
      setIsResolving(false)
      if (status !== "OK" || !place?.geometry?.location) return

      const location = place.geometry.location
      const lat = typeof location.lat === "function" ? location.lat() : location.lat
      const lng = typeof location.lng === "function" ? location.lng() : location.lng
      applyMapCenter(lat, lng, item.description || place.formatted_address)
    })
  }

  const geocodeTypedLocation = (rawText) => {
    const query = rawText?.trim()
    if (!query) return
    if (query === lastAppliedLabelRef.current) return
    if (typeof window === "undefined" || !window.google?.maps?.Geocoder) return

    setIsResolving(true)
    const geocoder = new window.google.maps.Geocoder()
    geocoder.geocode({ address: query }, (results, status) => {
      setIsResolving(false)
      if (status !== "OK" || !results?.[0]?.geometry?.location) return

      const location = results[0].geometry.location
      const lat = typeof location.lat === "function" ? location.lat() : location.lat
      const lng = typeof location.lng === "function" ? location.lng() : location.lng
      applyMapCenter(lat, lng, results[0].formatted_address || query)
    })
  }

  const predictionMatchesQuery = (item, query) => {
    const q = query.toLowerCase()
    const description = stripUSAFromString(item?.description || "").toLowerCase()
    if (!q || !description) return false
    const firstPart = description.split(",")[0]?.trim()
    if (!firstPart) return false
    return description.includes(q) || q.includes(firstPart)
  }

  const resolveTypedLocation = () => {
    const query = inputText.trim()
    if (!query) return
    if (query === lastAppliedLabelRef.current) return

    const matchingPrediction = placePredictions?.find((item) => predictionMatchesQuery(item, query))
    if (matchingPrediction) {
      applyPrediction(matchingPrediction)
      return
    }

    geocodeTypedLocation(query)
  }

  return (
    <Combobox
      as="div"
      className="sm:flex-1"
      value={inputText}
      onChange={(item) => {
        if (item) {
          skipBlurResolveRef.current = true
          applyPrediction(item)
        }
      }}>
      <div className="relative">
        <Combobox.Input
          onChange={(evt) => {
            setInputText(evt.target.value)
            getPlacePredictions({ input: evt.target.value })
          }}
          onKeyDown={(evt) => {
            if (evt.key === "Enter") {
              evt.preventDefault()
              resolveTypedLocation()
            }
          }}
          onBlur={() => {
            // Allow Combobox option click to win over blur geocode
            window.setTimeout(() => {
              if (skipBlurResolveRef.current) {
                skipBlurResolveRef.current = false
                return
              }
              resolveTypedLocation()
            }, 150)
          }}
          className={twMerge(BASE_INPUT_CLASSNAMES, "sm:py-[7px] min_sm:w-[260px] min_sm:rounded-l-none")}
          placeholder="Location"
        />
        {isPlacePredictionsLoading || isResolving ? (
          <LoadingSpinner className="absolute right-2 top-2 py-0" spinnerClassName="h-5 w-5" />
        ) : (
          <MapPinIcon className="absolute right-1.5 top-1.5 size-6 text-gray-dark opacity-[0.64] sm:top-[7px]" />
        )}
      </div>
      {placePredictions?.length > 0 && !isPlacePredictionsLoading && (
        <Combobox.Options
          className={twMerge(DROPDOWN_CLASSNAMES, "fixed w-[260px] sm:w-[calc(100%-48px)]")}
          style={{ maxWidth: isMobile ? 300 : 260 }}>
          {placePredictions.map((item) => (
            <Combobox.Option
              key={item.description}
              className={DROPDOWN_OPTION_CLASSNAMES}
              value={item}
              onMouseDown={() => {
                skipBlurResolveRef.current = true
              }}>
              <div
                dangerouslySetInnerHTML={{
                  __html: stripUSAFromString(item.description).replace(
                    new RegExp(inputText, "i"),
                    `<mark style='font-weight: bold'>${capitalize(inputText)}</mark>`
                  )
                }}
              />
            </Combobox.Option>
          ))}
        </Combobox.Options>
      )}
    </Combobox>
  )
}

export default LocationSearchBox
